import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { useTranslation } from 'react-i18next';
import { clearAuthData } from '../../../Config/ApiConfig';
import { fbLogout } from '../../../Utils/facebook';
import i18n from '../../../i18n';
import { useGlobal } from '../GlobalContext/GlobalContext';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocket from '../../../Hooks/useSocket';
import { Resource, Action } from '../../../Constants/socket';

const UserLanguageSync = () => {
  const { user, changeUser } = useGlobal();
  const userId = user?.id;
  const navigate = useNavigate();
  const { notification } = AntdApp.useApp();
  const { t } = useTranslation();
  const { socket } = useSocket();

  const userIdArray = useMemo(() => userId ? [userId] : null, [userId]);

  // Subscribe to the current user's resource over socket
  useSocketSubscription(Resource.USER, userIdArray);

  const onHandleLogout = useCallback(async () => {
    // Disconnect from socket before logout
    if (socket && socket.disconnect) {
      socket.disconnect();
    }

    await fbLogout();
    navigate('/login');
    clearAuthData();
  }, [navigate, socket]);

  // Handle USER:DELETE events (when account is actively deleted by admin)
  useSocketEvent(Resource.USER, Action.DELETE, useCallback((data) => {
    if (data?.id === userId) {
      notification.error({
        message: t('session_terminated', { defaultValue: 'Session Terminated' }),
        description: t('user_deleted_message', { defaultValue: 'Your account has been deleted by an administrator.' }),
      });
      onHandleLogout();
    }
  }, [userId, onHandleLogout, notification, t]));

  // Handle USER:UPDATE events (e.g., when language is changed from another client)
  useSocketEvent(Resource.USER, Action.UPDATE, useCallback((updatedUser) => {
    if (!updatedUser || !updatedUser.id || !userId || updatedUser.id !== userId) return;

    // Merge latest user data into global context (also updates localStorage via GlobalContext)
    changeUser((prevUser) => ({
      ...(prevUser || {}),
      ...updatedUser,
    }));

    const newLanguage = updatedUser.language;
    if (newLanguage && newLanguage !== i18n.language) {
      i18n.changeLanguage(newLanguage);
    }
  }, [userId, changeUser]));

  return null;
};

export default UserLanguageSync;

